import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, timeout, tap, switchMap, map } from 'rxjs/operators';
import { ImageService } from '../image.service';
import { LoggerService } from '../logger.service';
import { environment } from '../../../environments/environment';
import {
  ProductInterface,
  ProductResponseInterface,
  SearchFilters,
  MediaInterface,
  AuthResponseInterface,
  AuthResponseWithProviderInterface,
  LoginRequestInterface,
  RegisterRequestInterface,
  RefreshTokenRequestInterface,
  ForgotPasswordRequestInterface,
  ResetPasswordRequestInterface,
  ChangePasswordRequestInterface,
  UserInterface,
  ProviderProductInterface,
  ProviderProductFilters,
  ProviderProductResponse,
  CreateProductRequest,
  ProviderDetailedInterface,
  ProviderStatsInterface,
  UpdateProviderRequest,
  UpdateUserRequest,
  UpdateUserProviderRequest,
  UpdateProductRequest,
  ProviderInterface,
  // Novas interfaces para reservas, pagamentos e favoritos
  BookingInterface,
  CreateBookingRequest,
  BookingFilters,
  BookingResponse,
  CancelBookingRequest,
  PaymentInterface,
  CreatePaymentRequest,
  PaymentMethodsResponse,
  FavoriteInterface,
  FavoriteToggleResponse,
  FavoriteCheckResponse,
  // Interfaces para provider requests
  ProviderRequestInterface,
  CreateProviderRequestRequest,
  ProviderRequestFilters,
  ProviderRequestResponse,
  ProviderRequestStatistics,
  UpdateProviderRequestRequest,
  // Interfaces para sistema de mapas
  MapProvider,
  MapClustersResponse,
  MapProviderWithDistance,
  TouristPoint,
  AuxiliaryService,
  MapSearchResult,
  HiddenPoint,
  CreateHiddenPointRequest,
  UpdateHiddenPointRequest,
  // Novas interfaces para booking e favorites
  BookingRequestInterface,
  BookingResponseInterface,
  FavoriteResponseInterface,
  // Novas interfaces para estatísticas, reviews e notificações
  UserStatisticsInterface,
  ReviewInterface,
  PaginationInterface,
  WalletFee,
  NotificationInterface
} from './apiservicemodel';

@Injectable({
  providedIn: 'root'
})
export class Apiservice {
  /**
   * Buscar dados de um admin pelo id usando /admin/admins e filtrando pelo id
   */
  public getAdminById(id: number | string): Observable<any> {
    const endpoint = `${this.apiUrl}/admin/admins`;
    return this.http.get<UserInterface[]>(endpoint).pipe(
      map((admins: UserInterface[]) => admins.find(a => a.id == id))
    );
  }
  /**
   * Edita usuário via rota administrativa
   */
  public editUser(id: number | string, data: Partial<UserInterface>): Observable<Partial<UserInterface>> {
    const endpoint = `${this.apiUrl}/admin/edit/${id}`;
    return this.http.put(endpoint, data);
  }
  private apiUrl = environment.apiUrl;
  private REQUEST_TIMEOUT = environment.apiTimeout;

  constructor(
    private http: HttpClient,
    private imageService: ImageService,
    private logger: LoggerService
  ) { }

  public searchProducts(filters: SearchFilters): Observable<ProductResponseInterface> {
    const searchEndpoint = `${this.apiUrl}/produtos/search`;
    let params = new HttpParams();

    Object.keys(filters).forEach(key => {
      const filterKey = key as keyof SearchFilters;
      const value = filters[filterKey];
      if (value !== null && value !== undefined && value !== '') {
        params = params.append(filterKey, value.toString());
      }
    });

    if (!filters.page) {
      params = params.set('page', '1');
    }
    if (!filters.limit) {
      params = params.set('limit', '60');
    }

    return this.http.get<ProductResponseInterface>(searchEndpoint, { params });
  }
  public getProductBySlug(id: string): Observable<ProductInterface> {
    // Use the existing getProductById method
    return this.getProductById(id);
  }



  /**
   * Obtém a imagem primária de um produto
   * @deprecated Use ImageService.getPrimaryImage() instead
   */
  public getPrimaryImage(product: ProductInterface): string {
    return this.imageService.getPrimaryImage(product);
  }

  /**
   * Obtém todas as imagens de um produto ordenadas por display_order
   * @deprecated Use ImageService.getProductImages() instead
   */
  public getProductImages(product: ProductInterface): MediaInterface[] {
    if (!product.media || product.media.length === 0) {
      return [];
    }

    return product.media
      .filter(media => media.media_type === 'photo')
      .sort((a, b) => a.display_order - b.display_order);
  }

  /**
   * Obtém uma imagem específica por índice
   * @deprecated Use ImageService.getImageByIndex() instead
   */
  public getImageByIndex(product: ProductInterface, index: number): string {
    return this.imageService.getImageByIndex(product, index);
  }

  // ==================== AUTHENTICATION METHODS ====================

  /**
   * Registra um novo usuário
   */
  public register(userData: RegisterRequestInterface): Observable<AuthResponseInterface> {
    const endpoint = `${this.apiUrl}/auth/register`;
    return this.http.post<AuthResponseInterface>(endpoint, userData);
  }



  /**
   * Faz login do usuário
   */
  public login(credentials: LoginRequestInterface): Observable<AuthResponseInterface> {
    const endpoint = `${this.apiUrl}/auth/login`;
    return this.http.post<AuthResponseInterface>(endpoint, credentials);
  }

  /**
   * Registra um novo usuário via OAuth2
   * TODO: Atualizar endpoint quando a rota de registro OAuth2 estiver disponível no backend
   */
  public registerWithOAuth2(code: string): Observable<AuthResponseInterface> {
    // Envia o código de autorização para o backend processar o registro
    const endpoint = `${this.apiUrl}/auth/register/google`;
    const payload = {
      code: code
    };
    return this.http.post<AuthResponseInterface>(endpoint, payload);
  }

  /**
   * Faz login via OAuth2 usando a rota /auth/login/google
   * Envia o código de autorização para o backend processar
   */
  public loginWithOAuth2(code: string): Observable<AuthResponseInterface> {
    const endpoint = `${this.apiUrl}/auth/login/google`;
    // O redirect_uri DEVE ser exatamente o mesmo usado para obter o código
    const redirect_uri = `${window.location.origin}/auth/callback`;

    this.logger.log('ApiService.loginWithOAuth2 - Endpoint:', endpoint);
    this.logger.log('ApiService.loginWithOAuth2 - redirect_uri:', redirect_uri);
    this.logger.log(
      'ApiService.loginWithOAuth2 - Código (primeiro/último 10 chars):',
      code.substring(0, 10) + '...' + code.substring(Math.max(0, code.length - 10))
    );

    const payload = { code, redirect_uri };

    return this.http.post<AuthResponseInterface>(endpoint, payload).pipe(
      timeout(this.REQUEST_TIMEOUT),
      tap((resp) => {
        this.logger.log('ApiService.loginWithOAuth2 - Resposta recebida:', resp);
      }),
      catchError((error) => {
        this.logger.error('ApiService.loginWithOAuth2 - Erro na requisição:', error);
        this.logger.error('ApiService.loginWithOAuth2 - Status:', error.status);
        this.logger.error('ApiService.loginWithOAuth2 - Mensagem:', error.error?.message);
        return throwError(() => error);
      })
    );
  }

  /**
   * Verifica se o email já está em uso
   * TEMPORÁRIO: Simula verificação até backend implementar endpoint
   * Verifica tanto usuários normais quanto provedores
   */
  public checkEmailAvailability(email: string): Observable<{ available: boolean }> {
    return this.http.post<{ available: boolean }>(`${this.apiUrl}/auth/check-email`, { email });
  }

  /**
   * Atualiza o token de acesso
   */
  public refreshToken(refreshData: RefreshTokenRequestInterface): Observable<AuthResponseInterface> {
    const endpoint = `${this.apiUrl}/auth/refresh`;
    return this.http.post<AuthResponseInterface>(endpoint, refreshData);
  }

  /**
   * Faz logout do usuário
   */
  public logout(): Observable<void> {
    const endpoint = `${this.apiUrl}/auth/logout`;
    return this.http.post<void>(endpoint, {});
  }

  /**
   * Solicita redefinição de senha
   */
  public forgotPassword(emailData: ForgotPasswordRequestInterface): Observable<void> {
    const endpoint = `${this.apiUrl}/auth/forgot-password`;
    return this.http.post<void>(endpoint, emailData);
  }

  /**
   * Redefine a senha com token
   */
  public resetPassword(resetData: ResetPasswordRequestInterface): Observable<void> {
    const endpoint = `${this.apiUrl}/auth/reset-password`;
    return this.http.post<void>(endpoint, resetData);
  }

  /**
   * Altera a senha do usuário autenticado
   */
  public changePassword(passwordData: ChangePasswordRequestInterface): Observable<void> {
    const endpoint = `${this.apiUrl}/auth/change-password`;
    return this.http.post<void>(endpoint, passwordData);
  }

  /**
   * Atualiza o email do usuário autenticado
   */

  /**
   * Obtém o perfil do usuário atual com informações completas (incluindo provider_info)
   */
  public getCurrentUser(): Observable<AuthResponseWithProviderInterface> {
    const endpoint = `${this.apiUrl}/auth/me`;
    return this.http.get<AuthResponseWithProviderInterface>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Atualiza o perfil do usuário
   */
  public updateUserProfile(profileData: Partial<UserInterface>): Observable<UserInterface> {
    const endpoint = `${this.apiUrl}/users/me`;
    return this.http.patch<UserInterface>(endpoint, profileData)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Atualiza usuário por ID
   */
  public updateUser(id: number | string, data: UpdateUserRequest): Observable<UserInterface> {
    const endpoint = `${this.apiUrl}/users/${id}`;
    return this.http.patch<UserInterface>(endpoint, data);
  }

  /**
   * Atualiza usuário provider por ID usando rota específica
   */
  public updateUserProvider(id: number | string, data: UpdateUserProviderRequest): Observable<UserInterface> {
    const endpoint = `${this.apiUrl}/users/provider/edit/${id}`;
    return this.http.put<UserInterface>(endpoint, data);
  }

  /**
   * Obtém estatísticas do usuário
   */
  public getUserStatistics(): Observable<UserStatisticsInterface> {
    const endpoint = `${this.apiUrl}/users/me/statistics`;
    return this.http.get<UserStatisticsInterface>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém favoritos do usuário
   * Nota: A API retorna produtos diretamente, não favoritos com estrutura aninhada
   */
  public getUserFavorites(): Observable<ProductInterface[] | { data: ProductInterface[], pagination: PaginationInterface }> {
    const endpoint = `${this.apiUrl}/users/me/favorites`;
    return this.http.get<ProductInterface[] | { data: ProductInterface[], pagination: PaginationInterface }>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém reservas do usuário
   */
  public getUserBookings(filters: BookingFilters): Observable<BookingResponse> {
    const endpoint = `${this.apiUrl}/bookings/my-bookings`;
    let params = new HttpParams();

    Object.keys(filters).forEach(key => {
      const value = filters[key as keyof BookingFilters];
      if (value !== null && value !== undefined && value !== '') {
        params = params.append(key, value.toString());
      }
    });

    return this.http.get<BookingResponse>(endpoint, { params })
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém pagamentos do usuário
   */
  public getUserPayments(): Observable<PaymentInterface[]> {
    const endpoint = `${this.apiUrl}/payments/my-payments`;
    return this.http.get<PaymentInterface[]>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }



  /**
   * Obtém avaliações do usuário
   */
  public getUserReviews(page: number = 1, limit: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    const endpoint = `${this.apiUrl}/users/me/reviews`;
    return this.http.get<any>(endpoint, { params })
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém todas as avaliações com filtros opcionais
   */
  public getReviews(params?: any): Observable<{ data: ReviewInterface[], pagination: PaginationInterface }> {
    const endpoint = `${this.apiUrl}/reviews`;
    return this.http.get<{ data: ReviewInterface[], pagination: PaginationInterface }>(endpoint, { params })
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obtém notificações do usuário
   */
  public getUserNotifications(page: number = 1, limit: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    const endpoint = `${this.apiUrl}/users/me/notifications`;
    return this.http.get<any>(endpoint, { params })
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Marca notificação como lida
   */
  public markNotificationAsRead(notificationId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/users/me/notifications/${notificationId}/read`;
    return this.http.patch<any>(endpoint, {})
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Marca todas as notificações como lidas
   */
  public markAllNotificationsAsRead(): Observable<any> {
    const endpoint = `${this.apiUrl}/users/me/notifications/read-all`;
    return this.http.patch<any>(endpoint, {})
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }



  /**
   * Obtém informações de um usuário específico por ID
   */
  public getUserById(id: number): Observable<UserInterface> {
    const endpoint = `${this.apiUrl}/users/${id}`;
    return this.http.get<UserInterface>(endpoint);
  }

  // ==================== PROVIDER METHODS ====================

  /**
   * Status do provider do usuário logado (has_provider, status, verificação)
   */
  public getMyProviderStatus(): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/my-status`;
    return this.http.get<any>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Atualizar configurações do meu provider (owner/manager) */
  public updateMyProviderSettings(providerId: number, data: Partial<{ company_name: string; description: string; company_phone: string; address: string; website: string; }>): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/settings`;
    return this.http.patch<any>(endpoint, data)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Listar equipe do meu provider */
  public getProviderStaff(providerId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/staff`;
    return this.http.get<any[]>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Convidar usuário para meu provider */
  public inviteUserToProvider(providerId: number, payload: { email: string; role: string }): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/invite-user`;
    return this.http.post<any>(endpoint, payload)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Remover membro da equipe */
  public removeProviderStaff(providerId: number, userId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/staff/${userId}`;
    return this.http.delete<any>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Listar convites pendentes para mim */
  public getMyPendingInvitations(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/providers/invitations/pending`;
    return this.http.get<any[]>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Aceitar convite */
  public acceptInvitation(invitationId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/invitations/${invitationId}/accept`;
    return this.http.post<any>(endpoint, {})
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Rejeitar convite */
  public rejectInvitation(invitationId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/invitations/${invitationId}/reject`;
    return this.http.post<any>(endpoint, {})
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /** Listar convites enviados pelo meu provider */
  public getProviderInvitationsSent(providerId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/invitations`;
    return this.http.get<any[]>(endpoint)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Cria um novo provider
   */
  public createProvider(providerData: any, token?: string): Observable<any> {
    const endpoint = `${this.apiUrl}/providers`;

    let headers = {};
    if (token) {
      headers = {
        'Authorization': `Bearer ${token}`
      };
    }

    return this.http.post<any>(endpoint, providerData, { headers });
  }

  /**
   * Obtém o provider de um usuário específico
   */
  public getUserProvider(userId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/user/${userId}`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Lista providers com paginação e filtros
   */
  public listProviders(params: {
    page?: number;
    limit?: number;
    company_name?: string;
    is_verified?: boolean;
    address?: string;
    latitude?: string;
    longitude?: string;
    sort_by?: 'company_name' | 'created_at' | 'is_verified' | 'latitude' | 'longitude';
    sort_order?: 'ASC' | 'DESC';
  } = {}): Observable<any> {
    const endpoint = `${this.apiUrl}/providers`;
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return this.http.get<any>(endpoint, { params: httpParams });
  }

  /**
   * Retorna providers pendentes de aprovação
   */
  public getPendingProviders(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/providers/pending-approval`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Obtém provider por ID
   */
  public getProviderById(id: number | string): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${id}`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Obtém detalhes públicos do provedor
   */
  public getProviderPublicDetails(id: number | string): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${id}/public`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Atualiza provider
   */
  public updateProvider(id: number | string, data: UpdateProviderRequest): Observable<ProviderInterface> {
    const endpoint = `${this.apiUrl}/providers/${id}`;
    return this.http.patch<ProviderInterface>(endpoint, data);
  }

  /**
   * Deleta provider
   */
  public deleteProvider(id: number | string): Observable<void> {
    const endpoint = `${this.apiUrl}/providers/${id}`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Aprova ou rejeita um provedor
   */
  public approveProvider(id: number | string, approvalData: { is_verified: boolean; admin_notes: string }): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${id}/approve`;
    return this.http.patch<any>(endpoint, approvalData);
  }

  /**
   * Lista empresas existentes para seleção
   */
  public getCompanies(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/companies`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Busca permissões de uma role específica
   */
  public getRolePermissions(roleId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/permissions/role/${roleId}`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Lista todas as permissões disponíveis
   */
  public getAllPermissions(): Observable<any> {
    const endpoint = `${this.apiUrl}/permissions`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Atribui permissões a uma role
   */
  public assignPermissionsToRole(roleId: number, permissions: number[]): Observable<any> {
    const endpoint = `${this.apiUrl}/permissions/assign-role`;
    return this.http.post<any>(endpoint, { role_id: roleId, permissions });
  }

  /**
   * Atualiza permissões de uma role
   */
  public updateRolePermissions(roleId: number, permissions: number[]): Observable<any> {
    const endpoint = `${this.apiUrl}/roles/${roleId}/permissions`;
    return this.http.put<any>(endpoint, { permissions });
  }

  /**
   * Cria uma nova role
   */
  public createRole(roleData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/roles`;
    return this.http.post<any>(endpoint, roleData);
  }

  /**
   * Lista usuários de uma role específica
   */
  public getRoleUsers(roleId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/roles/${roleId}/users`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Remove um usuário de uma role
   */
  public removeUserFromRole(userId: number, roleId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/roles/remove-user`;
    return this.http.post<any>(endpoint, { user_id: userId, role_id: roleId });
  }

  /**
   * Criar uma nova avaliação
   */
  public createReview(reviewData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/reviews`;
    return this.http.post<any>(endpoint, reviewData);
  }

  /**
   * Atualizar uma avaliação existente
   */
  public updateReview(reviewId: number, reviewData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/reviews/${reviewId}`;
    return this.http.patch<any>(endpoint, reviewData)
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  public getRoleById(roleId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/roles/${roleId}`;
    return this.http.get<any>(endpoint);
  }

  public updateRole(roleId: number, roleData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/roles/${roleId}`;
    return this.http.put<any>(endpoint, roleData);
  }

  public getProvinces(): Observable<any> {
    const endpoint = `${this.apiUrl}/provinces`;
    return this.http.get<any>(endpoint);
  }

  public getDistricts(): Observable<any> {
    const endpoint = `${this.apiUrl}/districts`;
    return this.http.get<any>(endpoint);
  }

  public getDistrictsByProvince(provinceId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/provinces/${provinceId}/districts`;
    return this.http.get<any>(endpoint);
  }

  public updateProvince(id: number, data: { country_id?: number; name?: string; image?: string; is_active?: boolean }): Observable<any> {
    const endpoint = `${this.apiUrl}/provinces/${id}`;
    return this.http.patch<any>(endpoint, data);
  }

  public getCountries(): Observable<any> {
    const endpoint = `${this.apiUrl}/countries`;
    return this.http.get<any>(endpoint);
  }

  public getRoles(): Observable<any> {
    const endpoint = `${this.apiUrl}/roles`;
    return this.http.get<any>(endpoint);
  }

  // ==================== ACCESS REQUESTS METHODS ====================

  /**
   * Solicitar acesso administrativo
   */
  public requestAccess(requestData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/request`;
    return this.http.post<any>(endpoint, requestData);
  }

  // ==================== MESSAGING METHODS ====================

  /**
   * Enviar uma mensagem para um provedor/usuário
   */
  public sendMessage(payload: import('./apiservicemodel').SendMessageRequest): Observable<any> {
    const endpoint = `${this.apiUrl}/messages`;
    return this.http.post<any>(endpoint, payload);
  }

  /**
   * Obter lista de conversas do turista (provedores com quem conversou)
   */
  public getTouristConversations(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/messages/tourist/conversations`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Obter mensagens entre o turista logado e um provedor
   */
  public getTouristMessages(providerId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/messages/tourist/${providerId}`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Obter lista de conversas do provedor logado
   */
  public getProviderConversations(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/messages/provider/conversations`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Obter mensagens de uma conversa específica (visão do provedor)
   */
  public getProviderMessages(providerId: number, touristId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/messages/provider/${providerId}/${touristId}`;
    return this.http.get<any[]>(endpoint);
  }


  /**
   * Listar minhas solicitações de acesso
   */
  public getMyRequests(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/access-requests/my-requests`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Listar todas as solicitações de acesso (Admin/Content Manager)
   */
  public getAllRequests(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/access-requests/all`;
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Listar solicitações de acesso com filtros (Admin/Content Manager)
   */
  public getFilteredRequests(params: {
    request_type?: string;
    status?: string;
    requested_by_id?: number;
    provider_id?: number;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/filtered`;
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return this.http.get<any>(endpoint, { params: httpParams });
  }

  /**
   * Estatísticas de solicitações de acesso (Admin/Content Manager)
   */
  public getRequestStats(): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/stats`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Obter detalhes de uma solicitação específica
   */
  public getRequestById(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/${id}`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Revisar uma solicitação de acesso (Admin/Content Manager)
   */
  public reviewRequest(id: number, reviewData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/${id}/review`;
    return this.http.put<any>(endpoint, reviewData);
  }

  /**
   * Cancelar uma solicitação de acesso
   */
  public cancelRequest(id: number): Observable<void> {
    const endpoint = `${this.apiUrl}/access-requests/${id}/cancel`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Convidar usuário para um provider
   */
  public inviteToProvider(inviteData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/provider-invite`;
    return this.http.post<any>(endpoint, inviteData);
  }

  /**
   * Listar convites de provider
   */
  public getProviderInvites(providerId?: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/access-requests/provider-invites`;
    let httpParams = new HttpParams();
    if (providerId) {
      httpParams = httpParams.set('provider_id', providerId.toString());
    }
    return this.http.get<any[]>(endpoint, { params: httpParams });
  }

  /**
   * Aceitar convite de provider
   */
  public acceptProviderInvite(acceptData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/access-requests/provider-invite/accept`;
    return this.http.post<any>(endpoint, acceptData);
  }

  /**
   * Cancelar convite de provider
   */
  public cancelProviderInvite(id: number): Observable<void> {
    const endpoint = `${this.apiUrl}/access-requests/provider-invite/${id}`;
    return this.http.delete<void>(endpoint);
  }

  // ==================== ADMIN MANAGEMENT METHODS ====================

  /**
   * Criar conta administrativa (Super Admin apenas)
   */
  public createAdmin(adminData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/admin/create`;
    return this.http.post<any>(endpoint, adminData);
  }

  // ==================== PROVIDER PRODUCT MANAGEMENT METHODS ====================

  /**
   * Listar produtos do provider logado com filtros e paginação
   */


  public getMyProducts(filters: ProviderProductFilters = {}): Observable<ProviderProductResponse> {
    const endpoint = `${this.apiUrl}/produtos/provider/my-products`;
    let httpParams = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    // Set default values

    if (!filters.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!filters.limit) {
      httpParams = httpParams.set('limit', '10');
    }
    if (!filters.status) {
      httpParams = httpParams.set('status', 'all');
    }
    if (!filters.sort_by) {
      httpParams = httpParams.set('sort_by', 'created_at');
    }
    if (!filters.sort_order) {
      httpParams = httpParams.set('sort_order', 'DESC');
    }
    if (!filters.category_id) {
      httpParams = httpParams.set('sort_order', 'DESC')
    }
    if (!filters.subcategory_id) {
      httpParams = httpParams.set('sort_order', 'DESC')
    }

    return this.http.get<ProviderProductResponse>(endpoint, { params: httpParams });
  }

  /**
   * Listar produtos de um provider específico com filtros avançados
   */
  public getProviderProducts(providerId: number, filters: ProviderProductFilters & {
    type?: 'servico' | 'atracao';
    min_price?: number;
    max_price?: number;
  } = {}): Observable<ProviderProductResponse> {
    const endpoint = `${this.apiUrl}/produtos/provider/${providerId}/products`;
    let httpParams = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    // Set default values
    if (!filters.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!filters.limit) {
      httpParams = httpParams.set('limit', '10');
    }
    if (!filters.status) {
      httpParams = httpParams.set('status', 'published');
    }
    if (!filters.sort_by) {
      httpParams = httpParams.set('sort_by', 'created_at');
    }
    if (!filters.sort_order) {
      httpParams = httpParams.set('sort_order', 'DESC');
    }

    return this.http.get<ProviderProductResponse>(endpoint, { params: httpParams });
  }

  /**
   * Criar novo produto com imagens base64
   */
  public createProduct(productData: CreateProductRequest): Observable<ProviderProductInterface> {
    const endpoint = `${this.apiUrl}/produtos/provider/create`;

    const formData = new FormData();

    // Campos escalares
    const scalarFields = [
      'name', 'slug', 'subcategory_id', 'description', 'short_description',
      'address', 'latitude', 'longitude', 'average_price', 'average_duration',
      'min_age', 'max_capacity', 'requirements', 'type', 'is_available',
      'is_featured', 'booking_url', 'category_id', 'city_id', 'district_id',
      'start_date', 'end_date', 'start_time', 'end_time', 'week_days',
      'capacity', 'price', 'requires_payment', 'requires_reservation', 'requires_checkin'
    ] as const;

    scalarFields.forEach(field => {
      const value = (productData as any)[field];
      if (value !== null && value !== undefined) {
        formData.append(field, String(value));
      }
    });

    // Amenidades — cada item separado para o backend receber como array
    (productData.amenities || []).forEach(a => formData.append('amenities', a));

    // Cancellation policy como JSON string
    formData.append('cancellation_policy', JSON.stringify(productData.cancellation_policy || {}));

    // Ficheiros de imagem
    if (productData.imageFiles?.length) {
      productData.imageFiles.forEach(file => {
        formData.append('images', file, file.name);
      });
    }

    return this.http.post<ProviderProductInterface>(endpoint, formData);
  }

  /**
   * Duplicar produto existente (backend copia imagens automaticamente)
   */
  public duplicateProduct(productId: number): Observable<ProviderProductInterface> {
    const endpoint = `${this.apiUrl}/produtos/provider/duplicate/${productId}`;
    return this.http.post<ProviderProductInterface>(endpoint, {});
  }

  /**
   * Atualizar produto existente
   */
  public updateProduct(productId: number, productData: UpdateProductRequest): Observable<ProviderProductInterface> {
    const endpoint = `${this.apiUrl}/produtos/${productId}`;
    return this.http.put<ProviderProductInterface>(endpoint, productData);
  }

  /**
   * Deletar produto
   */
  public deleteProduct(productId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/produtos/${productId}`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Obter informações detalhadas do provider com estatísticas completas
   */
  public getProviderDetailed(id: number): Observable<ProviderDetailedInterface> {
    const endpoint = `${this.apiUrl}/providers/${id}/detailed`;
    return this.http.get<ProviderDetailedInterface>(endpoint);
  }

  /**
   * Obter estatísticas agregadas do provider
   */
  public getProviderStats(id: number): Observable<ProviderStatsInterface> {
    const endpoint = `${this.apiUrl}/providers/stats/${id}`;
    return this.http.get<ProviderStatsInterface>(endpoint);
  }

  /**
   * Busca avançada de produtos com múltiplos filtros
   */
  public searchProductsAdvanced(filters: SearchFilters & {
    provider_id?: number;
    type?: 'servico' | 'atracao';
    min_age?: number;
    max_capacity?: number;
    min_duration?: number;
    max_duration?: number;
  }): Observable<ProductResponseInterface> {
    const endpoint = `${this.apiUrl}/produtos/search`;
    let httpParams = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    // Set default values
    if (!filters.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!filters.limit) {
      httpParams = httpParams.set('limit', '10');
    }

    return this.http.get<ProductResponseInterface>(endpoint, { params: httpParams });
  }

  /**
   * Listar todos os produtos (endpoint público)
   */
  public getAllProducts(): Observable<ProductInterface[]> {
    const endpoint = `${this.apiUrl}/produtos`;
    return this.http.get<ProductInterface[]>(endpoint);
  }

  /**
   * Buscar produto por ID (endpoint público)
   */
  public getProductById(id: string): Observable<ProductInterface> {
    const endpoint = `${this.apiUrl}/produtos/${id}`;
    return this.http.get<ProductInterface>(endpoint);
  }

  /**
   * Registrar visualização de um produto
   */
  public trackProductView(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/produtos/${id}/view`;
    return this.http.post(endpoint, {});
  }

  /**
   * Listar providers para seleção (com informações do proprietário)
   */
  public getProvidersForSelection(params: {
    search?: string;
    page?: number;
    limit?: number;
    is_verified?: boolean;
  } = {}): Observable<{
    data: Array<{
      id: number;
      company_name: string;
      owner_user_id: number;
      owner_name: string;
      owner_email: string;
      is_verified: boolean;
      address: string;
      company_phone: string;
      company_email: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasPrevious: boolean;
      hasNext: boolean;
    };
  }> {
    const endpoint = `${this.apiUrl}/providers/selection`;
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    // Set default values
    if (!params.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!params.limit) {
      httpParams = httpParams.set('limit', '10');
    }

    return this.http.get<{
      data: Array<{
        id: number;
        company_name: string;
        owner_user_id: number;
        owner_name: string;
        owner_email: string;
        is_verified: boolean;
        address: string;
        company_phone: string;
        company_email: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasPrevious: boolean;
        hasNext: boolean;
      };
    }>(endpoint, { params: httpParams });
  }

  /**
   * Listar usuários para seleção (para criar provider)
   */
  public getUsersForSelection(params: {
    search?: string;
    page?: number;
    limit?: number;
    without_provider?: boolean;
    user_type?: string;
    is_active?: boolean;
  } = {}): Observable<{
    data: Array<{
      id: number;
      name: string;
      email: string;
      user_type: string;
      phone: string;
      is_active: boolean;
      email_verified: boolean;
      created_at: string;
      has_provider: boolean;
      existing_provider_id: number | null;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasPrevious: boolean;
      hasNext: boolean;
    };
  }> {
    const endpoint = `${this.apiUrl}/users/selection`;
    let httpParams = new HttpParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    // Set default values
    if (!params.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!params.limit) {
      httpParams = httpParams.set('limit', '10');
    }

    return this.http.get<{
      data: Array<{
        id: number;
        name: string;
        email: string;
        user_type: string;
        phone: string;
        is_active: boolean;
        email_verified: boolean;
        created_at: string;
        has_provider: boolean;
        existing_provider_id: number | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasPrevious: boolean;
        hasNext: boolean;
      };
    }>(endpoint, { params: httpParams });
  }

  /**
   * Criar novo usuário
   */
  public createUser(userData: {
    name: string;
    email: string;
    password: string;
    user_type: string;
    phone?: string;
    avatar?: string;
    preferred_language?: string;
    role_id?: number;
    is_active?: boolean;
    nationality?: string;
    document_id?: string;
    document_type?: string;
    tourist_interests?: string[] | string;
    terms_accepted?: boolean;
    // Admin/extra fields support
    last_name?: string;
    country_id?: number;
    province_id?: number;
    district_id?: number;
    nuit?: string;
    type?: string;
    access_request_id?: number;
  }): Observable<{
    id: number;
    name: string;
    email: string;
    user_type: string;
    phone: string;
    is_active: boolean;
    email_verified: boolean;
    created_at: string;
  }> {
    const endpoint = `${this.apiUrl}/users/provider`;
    return this.http.post<{
      id: number;
      name: string;
      email: string;
      user_type: string;
      phone: string;
      is_active: boolean;
      email_verified: boolean;
      created_at: string;
    }>(endpoint, userData);
  }

  /**
   * Listar usuários com paginação e filtros
   */
  public listUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    user_type?: string;
    is_active?: boolean | number;
    email_verified?: boolean | number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Observable<{
    data: Array<{
      id: number;
      name: string;
      email: string;
      user_type: string;
      phone?: string;
      avatar?: string;
      preferred_language?: string;
      is_active: boolean | number;
      email_verified: boolean | number;
      last_login?: string;
      nationality?: string;
      document_id?: string;
      document_type?: string;
      terms_accepted?: boolean | number;
      created_at: string;
      updated_at: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasPrevious: boolean;
      hasNext: boolean;
    };
  }> {
    const endpoint = `${this.apiUrl}/users`;
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    if (!params.page) {
      httpParams = httpParams.set('page', '1');
    }
    if (!params.limit) {
      httpParams = httpParams.set('limit', '10');
    }

    return this.http.get<{
      data: Array<any>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasPrevious: boolean;
        hasNext: boolean;
      };
    }>(endpoint, { params: httpParams });
  }

  // ===== SISTEMA DE RESERVAS (BOOKINGS) =====

  /**
   * Criar nova reserva
   */
  public createBooking(bookingData: BookingRequestInterface): Observable<BookingResponseInterface> {
    const endpoint = `${this.apiUrl}/bookings`;
    return this.http.post<BookingResponseInterface>(endpoint, bookingData);
  }

  /**
   * Buscar reservas por produto para verificar disponibilidade
   */
  public getProductBookings(productId: number): Observable<BookingResponse> {
    const endpoint = `${this.apiUrl}/bookings`;
    const params = new HttpParams()
      .set('produto_id', productId.toString())
      .set('limit', '100'); // Buscar mais reservas para ter uma visão completa

    return this.http.get<BookingResponse>(endpoint, { params });
  }



  public getProviderBookings(filters: BookingFilters = {}): Observable<BookingResponse> {
    // Usar provider_info do localStorage em vez de chamar /auth/me a cada requisição
    let providerId: number | null = null;
    try {
      const providerInfoStr = localStorage.getItem('provider_info');
      if (providerInfoStr) {
        const providerInfo = JSON.parse(providerInfoStr);
        providerId = providerInfo?.id || null;
      }
    } catch {
      providerId = null;
    }

    if (!providerId) {
      return of({ bookings: [], page: 1, limit: 10, total: 0, totalPages: 0 } as BookingResponse);
    }

    const endpoint = `${this.apiUrl}/bookings`;
    const cleanFilters: any = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'page' && key !== 'limit') {
        cleanFilters[key] = value;
      }
    });

    if (!cleanFilters.date_from) cleanFilters.date_from = '2020-01-01';
    if (!cleanFilters.date_to) cleanFilters.date_to = '2030-12-31';
    cleanFilters.limit = 100;

    let params = new HttpParams();
    Object.entries(cleanFilters).forEach(([key, value]) => {
      params = params.set(key, String(value));
    });

    return this.http.get<BookingResponse>(endpoint, { params }).pipe(
      map((response: BookingResponse) => {
        let filteredBookings: any[] = [];

        if (response.bookings?.length > 0) {
          filteredBookings = response.bookings.filter(booking => {
            const produtoProviderId = booking.produto?.provider?.id;
            const produtoProviderIdDirect = booking.produto?.provider_id;
            return produtoProviderId === providerId || produtoProviderIdDirect === providerId;
          });
        }

        const page = filters.page || 1;
        const limit = filters.limit || 10;
        const startIndex = (page - 1) * limit;
        const paginatedBookings = filteredBookings.slice(startIndex, startIndex + limit);
        const totalPages = Math.ceil(filteredBookings.length / limit);

        return {
          bookings: paginatedBookings,
          page,
          limit,
          total: filteredBookings.length,
          totalPages
        } as BookingResponse;
      }),
      catchError((error) => {
        this.logger.error('Erro ao carregar reservas do provedor:', error);
        return of({ bookings: [], page: 1, limit: 10, total: 0, totalPages: 0 } as BookingResponse);
      })
    );
  }
  /**
   * Listar reservas do usuário logado
   */
  public getMyBookings(filters: BookingFilters = {}): Observable<BookingResponse> {
    const endpoint = `${this.apiUrl}/bookings/my-bookings`;
    let params = new HttpParams();



    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));

      }
    });

    // Set default values
    if (!filters.page) {
      params = params.set('page', '1');

    }
    if (!filters.limit) {
      params = params.set('limit', '10');

    }

    // Definir datas padrão se não foram fornecidas
    if (!filters.date_from) {
      params = params.set('date_from', '2020-01-01');

    }
    if (!filters.date_to) {
      params = params.set('date_to', '2030-12-31');

    }


    return this.http.get<BookingResponse>(endpoint, { params });
  }

  public getProviderReviews(providerId: number, filters: any = {}): Observable<any> {
    const endpoint = `${this.apiUrl}/reviews/provider/${providerId}`;
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    // Set default values
    if (!filters.page) {
      params = params.set('page', '1');
    }
    if (!filters.limit) {
      params = params.set('limit', '10');
    }

    return this.http.get<any>(endpoint, { params });
  }

  /**
   * Obtém avaliações de um produto específico
   */
  public getProductReviews(produtoId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/reviews/produto/${produtoId}`;

    return this.http.get<any>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }
  /**
   * Obtém estatísticas das avaliações de um produto
   */
  public getProductReviewsStats(produtoId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/reviews/stats`;
    let params = new HttpParams();
    params = params.set('produto_id', produtoId.toString());

    return this.http.get<any>(endpoint, { params })
      .pipe(
        timeout(this.REQUEST_TIMEOUT),
        catchError(this.handleError)
      );
  }

  /**
   * Obter detalhes de uma reserva específica
   */
  public getBookingById(bookingId: number): Observable<BookingInterface> {
    const endpoint = `${this.apiUrl}/bookings/${bookingId}`;
    return this.http.get<BookingInterface>(endpoint);
  }

  /**
   * Confirmar reserva
   */
  public confirmBooking(bookingId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/bookings/${bookingId}/confirm`;
    return this.http.post<void>(endpoint, {});
  }

  /**
   * Cancelar reserva
   */
  public cancelBooking(bookingId: number, reason: CancelBookingRequest): Observable<void> {
    const endpoint = `${this.apiUrl}/bookings/${bookingId}/cancel`;
    return this.http.post<void>(endpoint, reason);
  }

  /* 
    Taxas de pagamento de uma reserva
   */
  public getWalletFees(): Observable<WalletFee[]> {
    const endpoint = `${this.apiUrl}/wallet-fees`;
    return this.http.get<any[]>(endpoint).pipe(
      map((res: any) => {
        const arr = Array.isArray(res) ? res : (res?.data ?? []);
        return arr.map((f: any) => ({
          id: f.id,
          paymentMethod: f.paymentMethod ?? f.payment_method,
          feePercentage: parseFloat(f.feePercentage ?? f.fee_percentage),
          enabled: f.enabled === 1 || f.enabled === true,
          created_at: f.created_at,
          updated_at: f.updated_at
        } as WalletFee));
      })
    );
  }

  public createWalletFee(payload: { paymentMethod: string, feePercentage: number, enabled: boolean }): Observable<WalletFee> {
    const endpoint = `${this.apiUrl}/wallet-fees`;
    return this.http.post<WalletFee>(endpoint, payload);
  }

  public updateWalletFee(id: number | string, payload: { paymentMethod: string, feePercentage: number, enabled: boolean }): Observable<WalletFee> {
    const endpoint = `${this.apiUrl}/wallet-fees/${id}`;
    return this.http.patch<WalletFee>(endpoint, payload);
  }

  /**
   * Processar pagamento para uma reserva
   */
  public processBookingPayment(bookingId: number, paymentData: CreatePaymentRequest): Observable<any> {
    const endpoint = `${this.apiUrl}/bookings/${bookingId}/payment`;
    return this.http.post<any>(endpoint, paymentData);
  }

  // ===== SISTEMA DE PAGAMENTOS (PAYMENTS) =====

  /**
   * Listar pagamentos do usuário logado
   */
  public getMyPayments(): Observable<PaymentInterface[]> {
    const endpoint = `${this.apiUrl}/payments/my-payments`;
    return this.http.get<PaymentInterface[]>(endpoint);
  }

  /**
   * Listar pagamentos de uma reserva específica
   */
  public getPaymentsByBooking(bookingId: number): Observable<PaymentInterface[]> {
    const endpoint = `${this.apiUrl}/payments/booking/${bookingId}`;
    return this.http.get<PaymentInterface[]>(endpoint);
  }

  /**
   * Buscar todas as reservas do sistema (apenas para admin)
   */
  public getAllBookings(filters: BookingFilters = {}): Observable<BookingResponse> {
    const endpoint = `${this.apiUrl}/bookings`;

    let params = new HttpParams();

    // Adicionar apenas os filtros suportados pelo endpoint
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (filters.payment_status) {
      params = params.set('payment_status', filters.payment_status);
    }
    if (filters.user_id) {
      params = params.set('user_id', String(filters.user_id));
    }
    if (filters.produto_id) {
      params = params.set('produto_id', String(filters.produto_id));
    }
    if (filters.date_from) {
      params = params.set('date_from', filters.date_from);
    }
    if (filters.date_to) {
      params = params.set('date_to', filters.date_to);
    }
    if (filters.page) {
      params = params.set('page', String(filters.page));
    }
    if (filters.limit) {
      params = params.set('limit', String(filters.limit));
    }

    // Valores padrão se não especificados
    if (!filters.page) {
      params = params.set('page', '1');
    }
    if (!filters.limit) {
      params = params.set('limit', '50');
    }
    if (!filters.date_from) {
      params = params.set('date_from', '2020-01-01');
    }
    if (!filters.date_to) {
      params = params.set('date_to', '2030-12-31');
    }

    return this.http.get<BookingResponse>(endpoint, { params });
  }

  /**
   * Obtém todas as reservas para admin
   */
  public getAdminBookings(filters: BookingFilters = {}): Observable<BookingResponse> {
    const endpoint = `${this.apiUrl}/admin/bookings`;

    // Criar objeto de parâmetros limpo
    const cleanFilters: any = {};

    // Adicionar apenas valores válidos
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && key !== 'page' && key !== 'limit') {
        cleanFilters[key] = value;
      }
    });

    // Definir paginação padrão
    const page = filters.page || 1;
    const limit = filters.limit || 50;

    // Criar HttpParams
    let params = new HttpParams()
      .set('page', String(page))
      .set('limit', String(limit));

    Object.entries(cleanFilters).forEach(([key, value]) => {
      params = params.set(key, String(value));
    });

    return this.http.get<BookingResponse>(endpoint, { params });
  }

  /**
   * Processar novo pagamento
   */
  public processPayment(paymentData: CreatePaymentRequest): Observable<any> {
    const endpoint = `${this.apiUrl}/payments/process`;
    return this.http.post<any>(endpoint, paymentData);
  }

  /**
   * Verificar status de um pagamento
   */
  public verifyPayment(
    payment:
      | { paymentId: number | string; paymentMethod: 'mpesa' | 'emola' | 'card' | 'paypal' }
      | number
      | string
  ): Observable<any> {
    const endpoint = `${this.apiUrl}/payments/verify`;
    const payload =
      typeof payment === 'object'
        ? payment
        : { paymentId: payment, paymentMethod: 'mpesa' as const };
    return this.http.post<any>(endpoint, payload);
  }

  /**
   * Listar métodos de pagamento suportados
   */
  public getSupportedPaymentMethods(): Observable<PaymentMethodsResponse> {
    const endpoint = `${this.apiUrl}/payments/methods/supported`;
    return this.http.get<PaymentMethodsResponse>(endpoint);
  }

  // ===== SISTEMA DE FAVORITOS (FAVORITES) =====

  /**
   * Listar favoritos do usuário logado
   * Nota: A API retorna produtos diretamente, não favoritos com estrutura aninhada
   */
  public getMyFavorites(): Observable<ProductInterface[]> {
    const endpoint = `${this.apiUrl}/users/me/favorites`;
    return this.http.get<ProductInterface[]>(endpoint);
  }

  /**
   * Adicionar produto aos favoritos
   */
  public addToFavorites(produtoId: number): Observable<FavoriteResponseInterface> {
    const endpoint = `${this.apiUrl}/users/me/favorites/${produtoId}`;
    return this.http.post<FavoriteResponseInterface>(endpoint, {});
  }

  /**
   * Remover produto dos favoritos
   */
  public removeFromFavorites(produtoId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/users/me/favorites/${produtoId}`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Verificar se um produto está nos favoritos
   * Retorna um objeto com {isFavorite: boolean}
   */
  public isFavorite(produtoId: number): Observable<FavoriteCheckResponse> {
    const endpoint = `${this.apiUrl}/users/me/favorites/${produtoId}`;
    return this.http.get<FavoriteCheckResponse>(endpoint);
  }

  /**
   * Alternar favorito (adicionar/remover)
   */
  public toggleFavorite(produtoId: number): Observable<FavoriteToggleResponse> {
    const endpoint = `${this.apiUrl}/users/me/favorites/${produtoId}/toggle`;
    return this.http.post<FavoriteToggleResponse>(endpoint, {});
  }

  /**
   * Verificar se produto está nos favoritos
   */
  public checkFavorite(produtoId: number): Observable<FavoriteCheckResponse> {
    const endpoint = `${this.apiUrl}/users/me/favorites/check/${produtoId}`;
    return this.http.get<FavoriteCheckResponse>(endpoint);
  }

  // ===== SISTEMA DE PROVIDER REQUESTS =====

  /**
   * Criar solicitação de interesse para se tornar provedor
   */
  public createProviderRequest(requestData: CreateProviderRequestRequest): Observable<ProviderRequestInterface> {
    const endpoint = `${this.apiUrl}/provider-requests`;
    return this.http.post<ProviderRequestInterface>(endpoint, requestData);
  }

  /**
   * Listar solicitações de provedor (apenas admin/superadmin)
   */
  public getProviderRequests(filters: ProviderRequestFilters = {}): Observable<ProviderRequestResponse> {
    const endpoint = `${this.apiUrl}/provider-requests`;
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    // Set default values
    if (!filters.page) {
      params = params.set('page', '1');
    }
    if (!filters.limit) {
      params = params.set('limit', '10');
    }

    return this.http.get<ProviderRequestResponse>(endpoint, { params });
  }

  /**
   * Obter estatísticas das solicitações
   */
  public getProviderRequestStatistics(): Observable<ProviderRequestStatistics> {
    const endpoint = `${this.apiUrl}/provider-requests/statistics`;
    return this.http.get<ProviderRequestStatistics>(endpoint);
  }

  /**
   * Obter detalhes de uma solicitação específica
   */
  public getProviderRequestById(requestId: number): Observable<ProviderRequestInterface> {
    const endpoint = `${this.apiUrl}/provider-requests/${requestId}`;
    return this.http.get<ProviderRequestInterface>(endpoint);
  }

  /**
   * Atualizar status de uma solicitação
   */
  public updateProviderRequest(requestId: number, updateData: UpdateProviderRequestRequest): Observable<ProviderRequestInterface> {
    const endpoint = `${this.apiUrl}/provider-requests/${requestId}`;
    return this.http.patch<ProviderRequestInterface>(endpoint, updateData);
  }

  /**
   * Obter serviços auxiliares para um produto
   */
  public getAuxiliaryServices(produtoId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/produtos/${produtoId}/auxiliary-services`;
    return this.http.get(endpoint);
  }

  /**
   * Obter produtos similares
   */
  public getSimilarProducts(produtoId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/produtos/${produtoId}/similar-products`;
    return this.http.get(endpoint);
  }

  // ===== SISTEMA DE MAPAS =====

  /**
   * Listar provedores para mapa
   */
  public getMapProviders(): Observable<MapProvider[]> {
    const endpoint = `${this.apiUrl}/maps/providers`;
    return this.http.get<MapProvider[]>(endpoint);
  }

  /**
   * Obter clusters de provedores
   */
  public getMapClusters(zoom?: number, bounds?: string): Observable<MapClustersResponse> {
    const endpoint = `${this.apiUrl}/maps/providers/clusters`;
    let params = new HttpParams();

    if (zoom) {
      params = params.set('zoom', zoom.toString());
    }
    if (bounds) {
      params = params.set('bounds', bounds);
    }

    return this.http.get<MapClustersResponse>(endpoint, { params });
  }

  /**
   * Buscar provedores próximos
   */
  public getNearbyProviders(lat: number, lng: number, radius: number = 10): Observable<MapProviderWithDistance[]> {
    const endpoint = `${this.apiUrl}/maps/providers/nearby`;
    const params = new HttpParams()
      .set('lat', lat.toString())
      .set('lng', lng.toString())
      .set('radius', radius.toString());

    return this.http.get<MapProviderWithDistance[]>(endpoint, { params });
  }

  /**
   * Listar pontos turísticos
   */
  public getTouristPoints(category?: string): Observable<TouristPoint[]> {
    const endpoint = `${this.apiUrl}/maps/tourist-points`;
    let params = new HttpParams();

    if (category) {
      params = params.set('category', category);
    }

    return this.http.get<TouristPoint[]>(endpoint, { params });
  }

  /**
   * Listar serviços auxiliares
   */
  public getAuxiliaryServicesMap(type?: string): Observable<AuxiliaryService[]> {
    const endpoint = `${this.apiUrl}/maps/auxiliary-services`;
    let params = new HttpParams();

    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<AuxiliaryService[]>(endpoint, { params });
  }

  /**
   * Busca inteligente no mapa
   */
  public searchMap(query: string, type?: string): Observable<MapSearchResult[]> {
    const endpoint = `${this.apiUrl}/maps/search`;
    let params = new HttpParams().set('q', query);

    if (type) {
      params = params.set('type', type);
    }

    return this.http.get<MapSearchResult[]>(endpoint, { params });
  }

  /**
   * Listar produtos públicos de um provedor
   */
  public getProviderPublicProducts(providerId: number, page: number = 1, limit: number = 10): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/public/products`;
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<any>(endpoint, { params });
  }

  /**
   * Listar categorias com contagem de produtos
   */
  public getCategories(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/produtos/categories`;
    return this.http.get<any[]>(endpoint);
  }

  // getSubcategories removido temporariamente - API não implementada

  /**
   * Listar todas as subcategorias
   * @param params Parâmetros opcionais de paginação (page, limit)
   */
  public getSubcategories(params?: { page?: number; limit?: number }): Observable<any> {
    let endpoint = `${this.apiUrl}/subcategories`;

    // Adicionar parâmetros de paginação se fornecidos
    if (params) {
      const queryParams: string[] = [];
      if (params.page !== undefined) queryParams.push(`page=${params.page}`);
      if (params.limit !== undefined) queryParams.push(`limit=${params.limit}`);
      if (queryParams.length > 0) {
        endpoint += `?${queryParams.join('&')}`;
      }
    }

    return this.http.get<any>(endpoint);
  }

  /**
   * Listar TODAS as subcategorias (sem limite de paginação)
   */
  public getAllSubcategories(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/subcategories?limit=1000`;
    return this.http.get<any>(endpoint).pipe(
      map((response: any) => {
        // Se a resposta tiver estrutura paginada, extrair os dados
        if (response && response.data && Array.isArray(response.data)) {
          return response.data;
        }
        // Se for um array direto
        if (Array.isArray(response)) {
          return response;
        }
        return [];
      })
    );
  }

  /**
   * Listar subcategorias por categoria específica
   */
  public getSubcategoriesByCategory(categoryId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/categories/${categoryId}/subcategories`;
    this.logger.log('Endpoint de subcategorias:', endpoint);
    return this.http.get<any[]>(endpoint);
  }

  /**
   * Atualizar uma categoria específica
   */
  public updateCategory(categoryId: number, categoryData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/categories/${categoryId}`;
    return this.http.patch<any>(endpoint, categoryData);
  }

  /**
   * Criar uma nova categoria
   */
  public createCategory(categoryData: {
    name: string;
    description?: string;
    icon?: string;
    is_active?: boolean;
  }): Observable<any> {
    const endpoint = `${this.apiUrl}/categories`;
    return this.http.post<any>(endpoint, categoryData);
  }

  /**
   * Criar uma nova subcategoria
   */
  public createSubcategory(subcategoryData: {
    category_id: number;
    name: string;
    description?: string;
    icon?: string;
    is_active?: boolean;
  }): Observable<any> {
    const endpoint = `${this.apiUrl}/subcategories`;
    return this.http.post<any>(endpoint, subcategoryData);
  }

  /**
   * Atualizar uma subcategoria específica
   */
  public updateSubcategory(subcategoryId: number, subcategoryData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/subcategories/${subcategoryId}`;
    return this.http.patch<any>(endpoint, subcategoryData);
  }

  /**
   * Deletar uma categoria específica
   */
  public deleteCategory(categoryId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/categories/${categoryId}`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Deletar uma subcategoria específica
   */
  public deleteSubcategory(subcategoryId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/subcategories/${subcategoryId}`;
    return this.http.delete<void>(endpoint);
  }


  // ===== SISTEMA DE PONTOS OCULTOS (SUPERADMIN) =====

  /**
   * Listar pontos ocultos
   */
  public getHiddenPoints(): Observable<HiddenPoint[]> {
    const endpoint = `${this.apiUrl}/maps/hidden-points`;
    return this.http.get<HiddenPoint[]>(endpoint);
  }

  /**
   * Criar ponto oculto
   */
  public createHiddenPoint(pointData: CreateHiddenPointRequest): Observable<HiddenPoint> {
    const endpoint = `${this.apiUrl}/maps/hidden-points`;
    return this.http.post<HiddenPoint>(endpoint, pointData);
  }

  /**
   * Obter ponto oculto por ID
   */
  public getHiddenPointById(pointId: number): Observable<HiddenPoint> {
    const endpoint = `${this.apiUrl}/maps/hidden-points/${pointId}`;
    return this.http.get<HiddenPoint>(endpoint);
  }

  /**
   * Atualizar ponto oculto
   */
  public updateHiddenPoint(pointId: number, updateData: UpdateHiddenPointRequest): Observable<HiddenPoint> {
    const endpoint = `${this.apiUrl}/maps/hidden-points/${pointId}`;
    return this.http.patch<HiddenPoint>(endpoint, updateData);
  }

  /**
   * Deletar ponto oculto
   */
  public deleteHiddenPoint(pointId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/maps/hidden-points/${pointId}`;
    return this.http.delete<void>(endpoint);
  }

  // ==================== USER MANAGEMENT METHODS (ADMIN) ====================

  /**
   * Listar todos os usuários (apenas admin/superadmin)
   */
  public getAllUsers(): Observable<{ users: UserInterface[] }> {
    const endpoint = `${this.apiUrl}/users`;
    return this.http.get<{ users: UserInterface[] }>(endpoint);
  }

  /**
   * Listar todos os administradores (apenas admin/superadmin)
   */
  public getAdmins(): Observable<{ users: UserInterface[] }> {
    const endpoint = `${this.apiUrl}/admin/admins`;
    return this.http.get<{ users: UserInterface[] }>(endpoint);
  }

  /**
   * Deletar usuário (apenas admin/superadmin)
   */
  public deleteUser(userId: number): Observable<void> {
    const endpoint = `${this.apiUrl}/admin/users/${userId}`;
    return this.http.delete<void>(endpoint);
  }

  /**
   * Atualizar status do usuário (apenas admin/superadmin)
   */
  public updateUserStatus(userId: number, isActive: boolean): Observable<UserInterface> {
    const endpoint = `${this.apiUrl}/users/${userId}/status`;
    return this.http.put<UserInterface>(endpoint, { is_active: isActive });
  }

  /**
   * Buscar atividades/ações de um usuário específico
   */
  public getUserActivity(userId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/users/${userId}/activity`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Buscar histórico de login de um usuário específico
   */
  public getUserLoginHistory(userId: number): Observable<any> {
    const endpoint = `${this.apiUrl}/users/${userId}/login-history`;
    return this.http.get<any>(endpoint);
  }

  /**
   * Buscar comentários de um usuário específico
   */
  public getUserComments(userId: number): Observable<any[]> {
    const endpoint = `${this.apiUrl}/users/${userId}/comments`;
    return this.http.get<any[]>(endpoint);
  }

  public updateProviderStatus(providerId: number, statusData: { is_active: boolean }): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/status`;
    return this.http.put<any>(endpoint, statusData);
  }

  /**
   * Associa usuários (um ou vários) a um provider existente como administradores/gestores.
   * Backend esperado: POST /providers/{id}/admin/assign-users
   * Payload típico: { user_ids: number[] }
   */
  public assignUsersToProvider(providerId: number, users: Array<{ userId: number; notes?: string }>): Observable<any> {
    const endpoint = `${this.apiUrl}/providers/${providerId}/admin/assign-users`;
    return this.http.post<any>(endpoint, { users });
  }

  /**
   * Obter estatísticas de usuários (apenas admin/superadmin)
   */
  public getUserStats(): Observable<{
    total_users: number;
    active_users: number;
    inactive_users: number;
    users_by_type: {
      tourist: number;
      provider: number;
      admin: number;
    };
    recent_registrations: number;
  }> {
    const endpoint = `${this.apiUrl}/admin/users/stats`;
    return this.http.get<{
      total_users: number;
      active_users: number;
      inactive_users: number;
      users_by_type: {
        tourist: number;
        provider: number;
        admin: number;
      };
      recent_registrations: number;
    }>(endpoint);
  }


  // Criar empresa
  public createCompany(companyData: any): Observable<any> {
    const endpoint = `${this.apiUrl}/providers`;
    return this.http.post<any>(endpoint, companyData);
  }

  /**
   * Conveniência frontend: criar usuário (provider) e empresa em uma única chamada sequencial.
   * Se a API backend suportar futuramente um endpoint único, substituir aqui.
   * Fluxo:
   * 1. POST /users/provider (cria usuário)
   * 2. POST /companies (cria empresa com owner_user_id = id do usuário)
   * 3. (Opcional) associar o usuário à company/provider se necessário por endpoint adicional
   */
  public createUserAndCompany(userData: any, companyForm: any): Observable<{ user: any; company: any; }> {
    return new Observable(observer => {
      this.createUser(userData).subscribe({
        next: (user) => {
          const ownerId = (user as any)?.id;
          const companyPayload = {
            owner_user_id: ownerId,
            company_name: companyForm.company_name,
            description: companyForm.company_description || 'Descrição da empresa',
            company_phone: companyForm.company_phone,
            company_email: companyForm.company_email,
            website: companyForm.company_website,
            service_type: companyForm.provider_type || 'hotel',
            nuit: companyForm.nuit,
            operation_license: 'LIC' + Math.random().toString(36).slice(2, 10),
            physical_address: companyForm.company_address || 'Endereço da empresa',
            social_media: { facebook: '', instagram: '' },
            service_description: companyForm.company_description || 'Descrição dos serviços',
            city: companyForm.city || '',
            province: companyForm.province || '',
            service_languages: ['português', 'inglês'],
            attached_documents: { license: '', certificate: '' },
            logo: '',
            address: companyForm.company_address || '',
            latitude: companyForm.latitude || -25.9692,
            longitude: companyForm.longitude || 32.5732,
            tax_number: companyForm.nuit,
            terms_conditions: companyForm.terms_conditions || 'Termos...'
          };
          this.createCompany(companyPayload).subscribe({
            next: (company) => {
              observer.next({ user, company });
              observer.complete();
            },
            error: (err) => {
              observer.error(err);
            }
          });
        },
        error: (err) => observer.error(err)
      });
    });
  }

  private handleError(error: any) {
    this.logger.error('API Error:', error);
    return throwError(() => new Error('API request failed'));
  }

  /**
   * Atualizar disponibilidade de um produto
   */
  public updateProductAvailability(productId: number | string, isAvailable: boolean): Observable<any> {
    const endpoint = `${this.apiUrl}/produtos/${productId}/availability`;
    const body = { is_available: isAvailable };
    return this.http.patch<any>(endpoint, body).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  // ========== CONFIGURATION METHODS ==========

  /**
   * Criar uma nova configuração
   */
  public createConfiguration(data: { configuration_type: string; value: string }): Observable<any> {
    const endpoint = `${this.apiUrl}/configurations`;
    return this.http.post<any>(endpoint, data).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Atualizar valor de uma configuração por tipo
   */
  public updateConfigurationByType(type: string, value: string): Observable<any> {
    const endpoint = `${this.apiUrl}/configurations/type/${type}`;
    return this.http.patch<any>(endpoint, { value }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Desativar uma configuração
   */
  public disableConfiguration(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/configurations/${id}/disable`;
    return this.http.put<any>(endpoint, {}).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Buscar todas as configurações (sem necessidade de token)
   */
  public getConfigurations(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/configurations`;
    return this.http.get<any[]>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Buscar metadata de última atualização das configurações
   */
  public getConfigurationsLastUpdated(): Observable<{ last_updated: string }> {
    const endpoint = `${this.apiUrl}/configurations/metadata/last-updated`;
    return this.http.get<{ last_updated: string }>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Buscar configuração por tipo
   */
  public getConfigurationByType(type: string): Observable<any> {
    const endpoint = `${this.apiUrl}/configurations/type/${type}`;
    return this.http.get<any>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Upload de mídia para configuração
   */
  public uploadConfigurationMedia(configurationTypeId: number, file: File): Observable<any> {
    const endpoint = `${this.apiUrl}/configuration-media/upload`;
    const formData = new FormData();
    formData.append('configuration_type_id', configurationTypeId.toString());
    formData.append('media', file);

    return this.http.post<any>(endpoint, formData).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Buscar todas as mídias de configuração
   */
  public getConfigurationMedias(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/configuration-media`;
    return this.http.get<any[]>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Deletar mídia de configuração
   */
  public deleteConfigurationMedia(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/configuration-media/${id}`;
    return this.http.delete<any>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  // ==================== LANGUAGES ====================

  /**
   * Listar todos os idiomas
   */
  public getLanguages(): Observable<any[]> {
    const endpoint = `${this.apiUrl}/languages`;
    return this.http.get<any[]>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Criar um novo idioma
   */
  public createLanguage(data: { code: string; name: string }): Observable<any> {
    const endpoint = `${this.apiUrl}/languages`;
    return this.http.post<any>(endpoint, data).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Atualizar um idioma
   */
  public updateLanguage(id: number, data: { code?: string; name?: string }): Observable<any> {
    const endpoint = `${this.apiUrl}/languages/${id}`;
    return this.http.put<any>(endpoint, data).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Remover um idioma
   */
  public deleteLanguage(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/languages/${id}`;
    return this.http.delete<any>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  // ==================== TRANSLATIONS ====================

  /**
   * Buscar traduções por página e idioma
   * @param page - Página (ex: 'homepage', 'product', 'category')
   * @param lang - Código do idioma (ex: 'pt', 'en')
   * @returns Observable com array de traduções
   * 
   * Exemplo de uso:
   * - getTranslations('homepage', 'en') -> GET /translations?page=homepage&lang=en
   * - getTranslations(undefined, 'en') -> GET /translations?lang=en
   * - getTranslations('homepage', undefined) -> GET /translations?page=homepage
   */
  public getTranslations(page?: string, lang?: string): Observable<any[]> {
    const endpoint = `${this.apiUrl}/translations`;
    let params = new HttpParams();

    // Validação: Se lang for fornecido, page também deve ser fornecido
    if (lang && !page) {
      // Retornar Observable vazio em vez de fazer a chamada
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    // Adicionar parâmetro 'page' se fornecido
    if (page) {
      params = params.set('page', page);
    }

    // Adicionar parâmetro 'lang' se fornecido
    if (lang) {
      params = params.set('lang', lang);
    }

    return this.http.get<any[]>(endpoint, { params }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Criar uma nova tradução
   */
  public createTranslation(data: { page: string; key: string; value: string; language_id: number }): Observable<any> {
    const endpoint = `${this.apiUrl}/translations`;
    return this.http.post<any>(endpoint, data).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Atualizar uma tradução
   */
  public updateTranslation(id: number, data: { page?: string; key?: string; value?: string; language_id?: number }): Observable<any> {
    const endpoint = `${this.apiUrl}/translations/${id}`;
    return this.http.put<any>(endpoint, data).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }

  /**
   * Remover uma tradução
   */
  public deleteTranslation(id: number): Observable<any> {
    const endpoint = `${this.apiUrl}/translations/${id}`;
    return this.http.delete<any>(endpoint).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError)
    );
  }
}



